import { emailAddressProblem } from './emailAddress';
import { normalizePhoneNumber } from './phoneNumber';

// Shared validation for the invite forms (inviting private persons and
// companies). Name and email are required; an optional phone must be valid.
// The user gets a clear message about
// what is expected when something is wrong.
//
// This returns i18n KEYS, not finished strings. The caller translates each key
// with the active-language `t` at render time, so an error that is on screen
// follows the language if the user switches between Swedish and English.

const HAS_LETTER = /\p{L}/u; // name must contain at least one letter

export interface InviteFieldNames {
  name: string;
  email: string;
  phone: string;
}

export interface InviteFieldValues {
  name: string;
  email: string;
  phone: string;
}

/**
 * Validate the name/email/phone of an invite form. Returns an object keyed by
 * the form's own field names whose values are i18n keys (translate with `t`
 * at render time). Empty when everything is valid.
 */
export function validateInviteFields(
  values: InviteFieldValues,
  fieldNames: InviteFieldNames
): Record<string, string> {
  const errors: Record<string, string> = {};

  const name = values.name.trim();
  if (!name) {
    errors[fieldNames.name] = 'validation.required';
  } else if (!HAS_LETTER.test(name)) {
    errors[fieldNames.name] = 'validation.name';
  }

  const email = values.email.trim();
  if (!email) {
    errors[fieldNames.email] = 'validation.required';
  } else {
    const problem = emailAddressProblem(email);
    if (problem) errors[fieldNames.email] = problem === 'unsupportedCharacters' ? 'validation.emailCharacters' : 'validation.email';
  }

  // Phone is optional; validate national and international numbers consistently.
  const phone = values.phone.trim();
  if (phone && !normalizePhoneNumber(phone)) {
    errors[fieldNames.phone] = 'common.phone.invalid';
  }

  return errors;
}
